import React from "react";
import type { AspectId } from "./frame";
import type { Theme } from "./theme";
import type { Storyboard } from "./director";
import { Stage } from "./Stage";
import { beatToScene } from "./beats";
import { buildFilmTimeline, layersAt, type FilmTimeline } from "./transitions";
import type { BrandKit } from "./brand";
import { beatToStoryScene, beatToStorybookScene, beatToOverlayScene, deriveStorySet } from "./story";
import type { CharacterSpec } from "./visuals-character";

/**
 * Anivaze v4 — FilmPlayer. Renders the whole storyboard as ONE continuous video:
 * beats cross-fade / push into each other via the overlapped film timeline, so it
 * reads as an edited film, not a slideshow. Deterministic (pure fn of `t`).
 */
export const FilmPlayer: React.FC<{
  sb: Storyboard;
  theme: Theme;
  aspect: AspectId;
  t: number;
  guides?: boolean;
  timeline?: FilmTimeline;
  brand?: BrandKit | null;
  story?: boolean;
  cast?: CharacterSpec[];
  storybook?: boolean;
  overlay?: boolean;
}> = ({ sb, theme, aspect, t, guides, timeline, brand, story, cast, storybook, overlay }) => {
  const built = React.useMemo(() => buildFilmTimeline(sb), [sb]);
  const tl = timeline ?? built;
  const kind = theme.motion === "snappy" ? "push" : "fade";
  const layers = layersAt(tl, t, kind);
  // One SET for the whole story (continuity), derived from the full script.
  const storySet = React.useMemo(() => deriveStorySet(sb.beats), [sb]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {layers.map((l) => (
        <div
          key={l.index}
          style={{
            position: "absolute",
            inset: 0,
            opacity: l.opacity,
            transform: `translateX(${l.dx * 100}%) scale(${l.scale})`,
            willChange: "opacity, transform",
          }}
        >
          <Stage
            aspect={aspect}
            theme={theme}
            scene={overlay
              ? beatToOverlayScene(l.beat, { index: l.index, total: sb.beats.length, format: sb.format })
              : storybook
              ? beatToStorybookScene(l.beat, { index: l.index, total: sb.beats.length, format: sb.format })
              : story && cast && cast.length
              ? beatToStoryScene(l.beat, { index: l.index, total: sb.beats.length, format: sb.format, set: storySet }, cast)
              : beatToScene(l.beat, { index: l.index, total: sb.beats.length, format: sb.format })}
            t={l.localTime}
            showGuides={guides && l.opacity > 0.5}
            motion={false}
            brand={brand}
          />
        </div>
      ))}
    </div>
  );
};
